/** Altaro backup tab: RMM snapshot slug and display helpers. */

export const ALTARO_BACKUP_SLUG = 'altaro-backup-status'

export const ALTARO_BACKUP_TAB_DESCRIPTION =
  'Altaro VM Backup status from the Windows Application event log (last 7 days). Guest posture ignores backup-host self-events and escalates on latest backup status.'

export const ALTARO_EVENT_IDS = {
  5000: 'Successful backup',
  5001: 'Backup with warnings',
  5002: 'Failed backup',
  5003: 'Successful restore or verification',
  5004: 'Failed restore or verification',
  5005: 'Successful offsite copy',
  5007: 'Failed offsite copy',
}

export function altaroBackupSnapshotFromRmmData(data) {
  return (data?.snapshots ?? []).find((s) => s.script_slug === ALTARO_BACKUP_SLUG) ?? null
}

export function normaliseAltaroStatus(status) {
  const text = String(status || '').toLowerCase()
  if (text === 'success') return 'success'
  if (text === 'warning') return 'warning'
  if (text === 'failed') return 'failed'
  if (text === 'missing') return 'missing'
  return text || 'unknown'
}

export function altaroStatusBadgeVariant(status) {
  const text = normaliseAltaroStatus(status)
  if (text === 'success') return 'success'
  if (text === 'warning') return 'warning'
  if (text === 'failed' || text === 'missing') return 'destructive'
  return 'secondary'
}

export function altaroStatusLabel(status, fallbackLabel) {
  if (fallbackLabel) return fallbackLabel
  const text = normaliseAltaroStatus(status)
  if (text === 'success') return 'Success'
  if (text === 'warning') return 'Warning'
  if (text === 'failed') return 'Failed'
  if (text === 'missing') return 'Not in backup'
  return status || '—'
}

export function asAltaroEventArray(value) {
  if (value == null) return []
  if (Array.isArray(value)) return value
  // PowerShell sometimes serialises an empty list as {}.
  if (typeof value === 'object') {
    if (Object.keys(value).length === 0) return []
    return [value]
  }
  return []
}

function nameKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
}

/**
 * Re-score Altaro snapshot posture for device Backup tab display.
 * Mirrors backend backup_service scoring: ignore host self-events; FAIL on latest backup only.
 */
export function scoreAltaroPayload(payload, { hostname = '', expectedVms = [] } = {}) {
  if (!payload) {
    return { overall_status: null, interpretation: null, vm_summary: [] }
  }

  const hostKey = nameKey(hostname)
  const expectedKeys = new Set(
    (expectedVms || []).map(nameKey).filter((key) => key && key !== hostKey),
  )
  const rows = asAltaroEventArray(payload.vm_summary)
  const failNames = []
  const warnNames = []

  const vm_summary = rows.map((row) => {
    const name = (row.vm_name || '').trim() || 'Unknown VM'
    const key = nameKey(row.vm_name)
    const is_backup_host = Boolean(hostKey && key === hostKey)
    const counts_toward_status = expectedKeys.size
      ? !is_backup_host && (expectedKeys.has(key) || Boolean(row.is_missing))
      : !is_backup_host

    if (counts_toward_status) {
      const backup = normaliseAltaroStatus(row.last_backup_status)
      const offsite = normaliseAltaroStatus(row.last_offsite_status)
      if (row.is_missing || backup === 'failed' || backup === 'missing') {
        failNames.push(name)
      } else if (backup === 'warning' || offsite === 'failed') {
        warnNames.push(name)
      }
    }

    return { ...row, is_backup_host, counts_toward_status }
  })

  let overall_status = 'PASS'
  let interpretation =
    'No scored Altaro guest backup failures in the lookback window.'

  if (failNames.length) {
    const unique = [...new Set(failNames)]
    overall_status = 'FAIL'
    interpretation = `${unique.length} guest VM${unique.length === 1 ? '' : 's'} need attention for backup: ${unique.join(', ')}. Backup-host self-events are ignored.`
  } else if (warnNames.length) {
    const unique = [...new Set(warnNames)]
    overall_status = 'WARN'
    interpretation = `${unique.length} guest VM${unique.length === 1 ? '' : 's'} have backup warnings or failed offsite copies: ${unique.join(', ')}.`
  } else {
    const scored = vm_summary.filter((row) => row.counts_toward_status)
    if (scored.length) {
      interpretation = `Altaro guest backups look healthy for ${scored.length} scored guest VM${scored.length === 1 ? '' : 's'}.`
    } else if (vm_summary.some((row) => row.is_backup_host)) {
      interpretation =
        'Only backup-host self-events were found; guest VM backup posture is not escalated from host service noise.'
    }
  }

  return { overall_status, interpretation, vm_summary }
}
